import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import './App.css';
import { useRefCheck, TERMINAL_REF_CODES } from './hooks/useRefCheck';
import { useTelemetry } from './hooks/useTelemetry';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { ensureFresh } from './services/tbToken';
import useOccupancy from './hooks/useOccupancy';
import StaffPinEntry from './components/TaskManagement/StaffPinEntry';
import { getCustomerTheme } from './utils/headerTheme';
import TaskList from './components/TaskManagement/TaskList';
import SwaasScoreGauge from './components/SwaasScoreGauge';
import packageJson from '../package.json';
import { analytics } from './analytics';
import { MdMan, MdWoman, MdWc } from 'react-icons/md';
import ReconnectingBanner from './components/ReconnectingBanner';
import * as submissionQueue from './utils/submissionQueue';
import ServiceRequestApp from './ServiceRequestApp';
import TouchlessQR from './components/TouchlessQR';

const TASK_MGMT_FEATURE_DEFAULT = false; // <- single line global toggle

// Last-resort fallback when no per-tablet, per-customer, or per-deployment
// support phone is configured. Matches the number printed on MyHub kiosks
// in the field. Resolution order on the client:
//   1. AndroidDevice.getSupportPhone() — per-tablet admin override (kiosk)
//   2. branding.supportPhone — per-customer Octacle SERVER_SCOPE attribute
//   3. config.admin_support.phone — bundled config.json fallback
//   4. MYHUB_DEFAULT_SUPPORT_PHONE — this constant
const MYHUB_DEFAULT_SUPPORT_PHONE = '+91 91647 42014';

// Length rules for the kiosk name-capture inputs (gated by feature_collectName /
// feature_require{,First,Last}Name). First name requires 3+ alphabets to deter test/junk
// input ("ab", "qq"); last name allows 1+ since initials are common
// ("Rajeev S."). Both capped at 20 to prevent paste-bomb abuse.
const FIRST_NAME_MIN_LEN = 3;
const LAST_NAME_MIN_LEN = 1;
const NAME_MAX_LEN = 20;

// Fallback copy for the name inputs, used when the customer sets no
// firstNameLabel / lastNameLabel attribute in Octacle. The last-name
// placeholder deliberately differs from its label — "(or initial)" hints at
// the 1-char minimum in a way the bare label doesn't.
const FIRST_NAME_FIELD = { label: 'First name', placeholder: 'First name' };
const LAST_NAME_FIELD = { label: 'Last name', placeholder: 'Last name (or initial)' };

// A field keeps the letters-only rule for as long as it is still a name — that
// is what keeps "R2D2"-style junk out. Once a customer relabels it, it isn't a
// name any more ("R&D", "IT-Ops", "Bldg 4"), and the letters-only filter would
// silently eat those characters as the user types. So a relabelled field takes
// a wider allowlist and a longer cap ("Information Technology" is 22, past the
// 20 a name gets). Both remain allowlists — no angle brackets, no quotes.
const NAME_CHAR_STRIP = /[^A-Za-z ]/g;
const GENERIC_CHAR_STRIP = /[^A-Za-z0-9 &.'()\/-]/g; // eslint-disable-line no-useless-escape
const GENERIC_MAX_LEN = 40;

// Module-level cache for config.json (survives StrictMode remounts)
const configCache = { promise: null, result: null };

// Stable empty-array reference (avoids re-firing dep-array effects on every render).
const EMPTY_ARRAY = [];

const positiveReasons = [
  'Overall Experience was Great',
  'Loved the Hands-Free Operation',
  'Awesome to see Action taken on my Feedback',
  'Others',
];

const negativeReasons = [
  'Paper Towels Unavailable',
  'Liquid Soap Unavailable',
  'Toilet Roll Unavailable',
  'Trash Bin not Cleaned',
  'Floor not Clean',
  'Bad Odor',
  'Broken Fixtures',
  'Others',
];

const FRIENDLY = {
  MISSING_REFID: {
    title: "Sorry, we couldn't load the Application",
    icon: '❌',
    lead: 'The link is missing a required reference.',
    bullets: [
      'If using a QR code, try rescanning the QR code at the washroom entrance.',
      'If using a saved link, retry with the latest valid link.',
    ],
  },
  INVALID_REFID: {
    title: "Sorry, we couldn't load the Application",
    icon: '❌',
    lead: 'Unable to identify the correct washroom(s) from the link.',
    bullets: [
      'If using a QR code, try rescanning the QR code at the washroom entrance.',
      'If using a saved link, retry with the latest valid link.',
    ],
  },
  // The validation-failure codes below (UPSTREAM/TIMEOUT/EXCEPTION/NETWORK/
  // VALIDATOR_UNAVAILABLE) are no longer rendered as a blocking error screen;
  // the boot path falls through to the form and the ReconnectingBanner shows
  // instead. Entries kept for FriendlyError fallback only.
  VALIDATOR_UNAVAILABLE: {
    title: "Sorry, we couldn't load the Application",
    icon: '🔌',
    lead: 'Our validation service is temporarily unavailable.',
    bullets: ['Check your connection and retry.', 'If the problem continues, contact Facilities.'],
  },
  TIMEOUT: {
    title: "Sorry, we couldn't load the Application",
    icon: '🔌',
    lead: 'Validation took longer than expected.',
    bullets: ['Check your connection and retry.', 'If the problem continues, contact Facilities.'],
  },
  UPSTREAM: {
    title: "Sorry, we couldn't load the Application",
    icon: '🚧',
    lead: 'Service is temporarily unavailable.',
    bullets: ['Refresh this page and try again.', 'If the problem continues, contact Facilities.'],
  },
  EXCEPTION: {
    title: "Sorry, we couldn't load the Application",
    icon: '❌',
    lead: 'An unexpected error occurred.',
    bullets: ['Refresh this page and try again.', 'If the problem continues, contact Facilities.'],
  },
  NETWORK: {
    title: "Sorry, we couldn't load the Application",
    icon: '🔌',
    lead: 'Network error. Reconnecting…',
    bullets: [],
  },
  INVALID_SERVICEDESK_CONFIG: {
    title: "Sorry, we couldn't load the Application",
    icon: '❌',
    lead: 'There was a Technical Error with the provided link.',
    bullets: ['The device configuration is invalid.'],
  },
  DEFAULT: {
    title: "Sorry, we couldn't load the Application",
    icon: '❌',
    lead: 'Please try again.',
    bullets: ['Refresh this page and try again.', 'If the problem continues, contact Facilities.'],
  },
};

function SubmissionNotice({ variant = 'positive', onClose }) {
  // Auto dismiss after 4s; can also be closed via button
  useEffect(() => {
    const t = setTimeout(() => onClose?.(), 10000);
    return () => clearTimeout(t);
  }, [onClose]);

  const isVeryPositive = variant === 'veryPositive';
  const isNegative = variant === 'negative';

  // reuse existing styles: negative -> "neutral" (amber), others -> "positive" (green)
  const cardClass = isNegative ? 'neutral' : isVeryPositive ? 'veryPositive' : 'positive';
  const iconClass = isNegative ? 'neutral' : 'ok';
  const showConfetti = isVeryPositive; // confetti only for Excellent

  // Second line reassures the visitor their feedback is RECORDED — true whether
  // it was delivered immediately or queued offline for sync (both paths show this
  // same notice), so we never imply "sent" when the kiosk is offline.
  const COPY = {
    veryPositive: {
      title: 'Big Kudos for your feedback.',
      text: 'Your feedback has been recorded.',
    },
    positive: {
      title: 'Thanks much for your feedback.',
      text: 'Your feedback has been recorded.',
    },
    negative: {
      title: 'We sincerely appreciate your feedback.',
      text: 'Your feedback has been recorded.',
    },
  };
  const c = COPY[variant] || COPY.positive;

  return (
    <div className="submit-wrap">
      {showConfetti && (
        <div className="burst" aria-hidden>
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
      )}

      <div className={`submit-card ${cardClass}`} role="status" aria-live="polite">
        <div className={`submit-icon ${iconClass}`} aria-hidden>
          ✓
        </div>
        <h3 className="submit-title">{c.title}</h3>
        <p className="submit-text">{c.text}</p>
        <div className="submit-actions">
          <button className="submit-close" onClick={() => onClose?.()}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function FriendlyError({ code, supportPhone, variant = 'card' }) {
  const c = FRIENDLY[code] || FRIENDLY.DEFAULT;
  const hotline = supportPhone || MYHUB_DEFAULT_SUPPORT_PHONE;

  if (variant === 'card') {
    return (
      <div className="error-wrap">
        <div className="error-card error-card--stripe" role="alert" aria-live="polite">
          <h1 className="error-title">🥲 {c.title}</h1>
          <p className="error-lead lead">
            <span className="icon" aria-hidden>
              {c.icon || '❌'}
            </span>
            <span>{c.lead}</span>
          </p>
          {c.bullets?.length > 0 && (
            <ul className="error-bullets">
              {c.bullets.map((b, i) => (
                <li key={i}>👉 {b}</li>
              ))}
            </ul>
          )}
          {hotline && (
            <p className="error-muted muted">
              📞 Need further help? Call Support at <strong>{hotline}</strong>
            </p>
          )}
        </div>
      </div>
    );
  }
}

function TopProgress({ visible }) {
  if (!visible) return null;
  return (
    <div className="top-progress">
      <div className="bar" />
    </div>
  );
}

// 🆕 Helper to read refId from URL (accepts aliases)
function getRefIdFromUrl() {
  try {
    const url = new URL(window.location.href);
    const candidates = [
      url.searchParams.get('refId'),
      url.searchParams.get('referenceid'),
      url.searchParams.get('ref'),
    ];
    const found = candidates.find(Boolean);
    return (found || '').trim() || null;
  } catch {
    return null;
  }
}

// 🆕 Helper to read token from URL (for E2E testing — skips n8n login).
//
// SECURITY: tokens passed via URL query string leak into browser history,
// Referer headers on outbound requests, and any request log that captures
// request-URIs. The testing-time value of this shortcut does not outweigh
// that risk in a production build. Only honour the ?token= param in
// non-production builds or when an explicit opt-in env flag is set at
// build time. A production-built PWA served from servicedesk.swaas.myhub.co.in
// will always return null here, so no user can inject a TB JWT via URL.
function getTokenFromUrl() {
  const allowUrlToken =
    process.env.NODE_ENV !== 'production' ||
    process.env.REACT_APP_E2E_TOKEN_FROM_URL === 'true';
  if (!allowUrlToken) return null;
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get('token') || null;
  } catch {
    return null;
  }
}

function AppHeader({ locationLabel, branding }) {
  const [showLocationTooltip, setShowLocationTooltip] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  // Auto-hide tooltip after 5 seconds
  useEffect(() => {
    if (!showLocationTooltip) return;
    const timer = setTimeout(() => setShowLocationTooltip(false), 5000);
    return () => clearTimeout(timer);
  }, [showLocationTooltip]);

  // Self-heal the customer logo. onError below latches logoFailed=true; without
  // a reset the logo stays hidden forever once it fails to load — which happens
  // whenever the kiosk paints before the gateway is online (its very first load
  // is often offline). Reset when the URL changes or connectivity returns so the
  // <img> remounts and retries, instead of waiting for the next app restart.
  useEffect(() => { setLogoFailed(false); }, [branding?.logoUrl]);
  useEffect(() => {
    const onOnline = () => setLogoFailed(false);
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  // Only render the client logo when we have a resolved dynamic URL from Octacle.
  // Loading state (branding === undefined), errors, missing attribute, or broken URL
  // all render an invisible size-preserving placeholder to prevent layout shift and
  // to avoid flashing the wrong customer's logo.
  const hasResolvedLogo = !logoFailed && branding && branding.logoUrl;
  const clientLogoAlt = branding?.logoAlt || '';

  // Customer-level theme: primary color → header bg, navHeaderTheme → contrast.
  const { headerStyle, myhubLogo } = getCustomerTheme(branding);

  return (
    <div className="hero hero--two-rows" style={headerStyle}>
      {/* Logo row with title centered between logos */}
      <div className="hero-logos">
        {/* LEFT: client logo (dynamic per customer; blank placeholder until resolved) */}
        {hasResolvedLogo ? (
          <img
            src={branding.logoUrl}
            alt={clientLogoAlt}
            className="hero-logo hero-logo--sigma"
          
            decoding="async"
            fetchpriority="high"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <div
            className="hero-logo hero-logo--sigma"
            aria-hidden="true"
            style={{ visibility: 'hidden' }}
          />
        )}

        {/* CENTER: Title with location info */}
        <div className="hero-title-container">
          <h1 className="hero-title">
            <span className="title-text">Washroom Feedback</span>
            {locationLabel && (
              <div className="location-icon-wrapper">
                <button
                  className="location-sparkle-btn"
                  onClick={(e) => {
                    setShowLocationTooltip(!showLocationTooltip);
                    const btn = e.currentTarget;
                    btn.blur();
                    setTimeout(() => {
                      if (btn) btn.blur();
                    }, 50);
                  }}
                  aria-label="View location"
                  title={locationLabel}
                >
                  ✨
                </button>
                {showLocationTooltip && <div className="location-tooltip">{locationLabel}</div>}
              </div>
            )}
          </h1>
        </div>

        {/* RIGHT: powered by myHuB */}
        <div className="hero-right" aria-label="Powered by myHuB">
          <span className="hero-powered">Powered by</span>
          <img
            src={myhubLogo}
            alt="myHuB"
            className="hero-logo hero-logo--myhub"
            decoding="async"
          />
        </div>
      </div>
    </div>
  );
}

// Helper function to determine washroom icon based on name
const getWashroomIcon = (name, size = 32, color) => {
  const lowerName = (name || '').toLowerCase();
  if (lowerName.includes('men') && !lowerName.includes('women')) {
    return <MdMan size={size} color={color || '#3b82f6'} />;
  } else if (
    lowerName.includes('women') ||
    lowerName.includes('woman') ||
    lowerName.includes('ladies')
  ) {
    return <MdWoman size={size} color={color || '#ec4899'} />;
  } else {
    return <MdWc size={size} color={color || '#8b5cf6'} />;
  }
};

function App() {
  const [rating, setRating] = useState('');
  const [reasons, setReasons] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [additionalComment, setAdditionalComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [config, setConfig] = useState({});
  const configReady = config.solution && config.device_id && config.location;
  const API_BASE = (config?.api_base || process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');
  const [washroom, setWashroom] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastRating, setLastRating] = useState(null);
  const detailsRef = useRef(null); // top of reasons/comment section
  const submittingRef = useRef(false); // synchronous double-tap guard (state lags a render)
  const emojiRowRef = useRef(null); // where the emoji row sits
  const submitBtnRef = useRef(null); // submit button row
  const [washroomError, setWashroomError] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Task Management State
  const [showTaskManagement, setShowTaskManagement] = useState(false);
  const [taskAuthCompleted, setTaskAuthCompleted] = useState(false);
  const [staffInfo, setStaffInfo] = useState(null);

  // Feedback details expansion state
  const [showDetails, setShowDetails] = useState(false);

  // SWaaS Score carousel state for Floor assets
  const [carouselIndex, setCarouselIndex] = useState(0);
  // Track which washrooms have SWaaS score data
  const [washroomsWithData, setWashroomsWithData] = useState({});

  // Callback when SwaasScoreGauge reports data status
  const handleSwaasDataStatus = useCallback((assetId, hasData) => {
    setWashroomsWithData((prev) => ({ ...prev, [assetId]: hasData }));
  }, []);

  // Disable right-click context menu for kiosk mode
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  // Auto-refresh kiosk app daily at 3 AM
  useEffect(() => {
    const checkAndRefresh = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();

      // Refresh at 3:00 AM
      if (hours === 3 && minutes === 0) {
        console.log('Daily auto-refresh triggered at 3:00 AM');
        window.location.reload();
      }
    };

    // Poll every 45 seconds so the 3:00 AM daily-refresh window (one minute
    // wide) is guaranteed to fire at least once — 45s gives us one safe
    // extra check if the tick drifts or the tab is backgrounded briefly.
    const interval = setInterval(checkAndRefresh, 45000);

    return () => clearInterval(interval);
  }, []);

  // Whether config.json has been fetched (non-empty object), regardless of field values
  const configLoaded = Object.keys(config || {}).length > 0;

  // 🔍 Derived ID from config when available
  const showWashroomSelect = config?.features?.washroom_select === true;
  const requireRef = config?.features?.require_refid === true;
  const [refId] = useState(() => getRefIdFromUrl());
  const [urlToken] = useState(() => getTokenFromUrl()); // Token from URL (for E2E testing)

  const refState = useRefCheck(refId, urlToken); // calls /api/ref/validate under the hood (passes token if available)
 // Service-request kiosk mode — driven entirely by the serviceDeskMode                                                                                        
  // customer attribute in Octacle. No URL param fallback: the mode is                                                                                          
  // admin-controlled and cannot be spoofed by editing the URL.                                                                                                 
  const isServiceRequestMode = refState.data?.serviceDeskMode === 'service-request';  
  const backendLabel = refState.status === 'ok' ? refState.data?.label : null;
  // Branding is undefined while ref is still loading (→ render placeholder, no flash).
  // Once ref resolves, branding is either the resolved object or null (→ static fallback).
  const branding = refState.status === 'ok' ? (refState.data?.branding || null) : undefined;
  // Customer-level font (TB attr `font`) → CSS var applied at the .App root.
  const { appStyle } = getCustomerTheme(branding);
  const showOccupancy = (refState.data?.features?.occupancy ?? config?.features?.occupancy) !== false; // server attr overrides config
  // SWaaS score is opt-IN per customer: only shown when the ASSET or CUSTOMER
  // in TB has `feature_swaasScoreGauge = true`. Missing / false / anything
  // else → hidden, and SwaasScoreGauge never mounts, so /api/swaas-score
  // never fires. The server/refRoutes.js attribute reader already flattens
  // feature_* attrs into `features`, stripping the prefix.
  const showSwaasScore = refState.data?.features?.swaasScoreGauge === true;
  // Name capture is opt-IN per customer (TB CUSTOMER attr `feature_collectName`),
  // which controls VISIBILITY of the pair. Asset-scope override wins, mirroring
  // the rest of the feature-flag pattern.
  const collectName = refState.data?.features?.collectName === true;
  // Required-ness is per FIELD. `feature_requireName` stays the pair-level
  // default so existing customers are untouched; `feature_requireFirstName` /
  // `feature_requireLastName` override it for one field, which is what lets a
  // customer ask for e.g. a mandatory "Name" beside an optional "Department".
  // `??` and not `||`: the server coerces every feature_* attr to a real
  // boolean, so an explicit false must beat the pair default — only a missing
  // attribute (undefined) should fall through.
  const nameFeatures = refState.data?.features;
  const requireNamePair = nameFeatures?.requireName === true;
  const requireFirstName = collectName && (nameFeatures?.requireFirstName ?? requireNamePair);
  const requireLastName = collectName && (nameFeatures?.requireLastName ?? requireNamePair);
  // Labels come from the CUSTOMER attrs firstNameLabel / lastNameLabel, shipped
  // on the branding object by server/refRoutes.js. When a customer sets one, it
  // replaces both the label and the placeholder — the stock placeholder ("Last
  // name (or initial)") would contradict a relabelled field.
  const firstNameLabel = branding?.firstNameLabel || FIRST_NAME_FIELD.label;
  const lastNameLabel = branding?.lastNameLabel || LAST_NAME_FIELD.label;
  const firstNamePlaceholder = branding?.firstNameLabel || FIRST_NAME_FIELD.placeholder;
  const lastNamePlaceholder = branding?.lastNameLabel || LAST_NAME_FIELD.placeholder;
  // A relabelled field also loosens its input rules and stops advertising
  // itself to autofill as a given/family name — offering a saved surname for a
  // "Department" box is worse than offering nothing.
  const firstRelabelled = Boolean(branding?.firstNameLabel);
  const lastRelabelled = Boolean(branding?.lastNameLabel);
  const firstMaxLen = firstRelabelled ? GENERIC_MAX_LEN : NAME_MAX_LEN;
  const lastMaxLen = lastRelabelled ? GENERIC_MAX_LEN : NAME_MAX_LEN;
  const firstStrip = firstRelabelled ? GENERIC_CHAR_STRIP : NAME_CHAR_STRIP;
  const lastStrip = lastRelabelled ? GENERIC_CHAR_STRIP : NAME_CHAR_STRIP;

  // Get token refresh interval from config (default: 2 hours)
  const tokenRefreshHours = config?.tb_token_refresh_hours || 2;

  // Initialize token refresh hook. Refresh cadence is driven by the JWT's
  // own `exp` claim (5 min before expiry); the config-driven `tokenRefreshHours`
  // is now only a fallback for non-JWT tokens. `forceRefresh` is exposed so
  // on-401 handlers (e.g. SwaasScoreGauge) can refresh immediately instead of
  // waiting for the scheduled timer.
  // Seeds the singleton TB token store (services/tbToken) and schedules a
  // proactive refresh. `activeToken` is a re-render signal backed by the
  // singleton; all actual TB calls read the live token via ensureFresh().
  const { activeToken } = useTokenRefresh(
    refState.data?.tbToken,
    refState.data?.tbRefreshToken,
    API_BASE,
    tokenRefreshHours
  );

  // Initialize telemetry hook with serviceDesks from API response
  // serviceDesks contains device access tokens for Octacle telemetry API
  useTelemetry(refState.data?.serviceDesks, config, {
    ref_validation_status: refState.status === 'ok' ? 'ok' : (refState.code || 'failed'),
  });

  // KIOSK-02: Cache washroom info for offline display in native Android app
  // When validation succeeds, pass the label to the native app so it can show
  // cached info if the page fails to load on next retry (e.g., network down)
  useEffect(() => {
    if (refState.status === 'ok' && refState.data?.label && typeof window.AndroidDevice?.cacheWashroomInfo === 'function') {
      window.AndroidDevice.cacheWashroomInfo(refState.data.label);
      if (process.env.NODE_ENV !== 'production') {
        console.log('[App] KIOSK-02: Cached washroom info for offline display:', refState.data.label);
      }
    }
  }, [refState.status, refState.data?.label]);

  // Native-app token mirroring is handled inside the singleton (services/tbToken
  // persist()) — one place owns saving the token, so it's not duplicated here.

  // Background drain of any feedback submissions queued from a prior failed
  // network attempt. Idempotent — safe to re-fire when API_BASE changes.
  // Re-attach a FRESH tbToken at send time: we strip it at enqueue (it would be
  // stale on a long outage and only bloats storage), and the server needs a
  // valid token to run incident classification for negative feedback. On a
  // short outage ensureFresh() returns a usable token so the incident still
  // fires; on a very long one it may be null and the incident is simply skipped.
  useEffect(() => {
    if (!API_BASE) return;
    submissionQueue.start(API_BASE, {
      enrich: async (entry) => {
        if (entry.endpoint && entry.endpoint !== '/submitFeedback') return entry.payload;
        const token = await ensureFresh().catch(() => null);
        return token ? { ...entry.payload, tbToken: token } : entry.payload;
      },
    });
  }, [API_BASE]);

  // Get washrooms from API response (available when asset is Floor type).
  // Memoized so the array identity is stable while refState.data.washrooms
  // doesn't change — prevents the auto-select effect (below) from refiring
  // on every render.
  const washroomsFromApi = useMemo(
    () => (refState.status === 'ok' && refState.data?.washrooms ? refState.data.washrooms : []),
    [refState.status, refState.data?.washrooms]
  );

  // Show washroom selector ONLY when there's more than one option to pick.
  // With zero or one option the picker is pointless: zero means there's
  // nothing to pick, one means the auto-select effect (below) has already
  // chosen it and the user shouldn't have to confirm. The submit-time guard
  // (line ~677) is similarly skipped because shouldShowWashroomSelect is
  // false — washroom is non-empty (auto-set) and the form proceeds cleanly.
  const configWashroomCount =
    showWashroomSelect && config?.washroom_ids
      ? Object.keys(config.washroom_ids).length
      : 0;
  const shouldShowWashroomSelect =
    washroomsFromApi.length > 1 || configWashroomCount > 1;

  // Get washrooms that have SWaaS data
  const washroomsWithSwaasData = washroomsFromApi.filter((wr) => washroomsWithData[wr.id]);

  // Real-time occupancy from PIR sensor via ThingsBoard (gated by feature flag).
  // Use a stable EMPTY_ARRAY reference when disabled so useOccupancy's effect
  // (dep: washrooms) doesn't re-fire on every parent render. (PR #41 review.)
  const occupancyWashrooms = showOccupancy ? washroomsFromApi : EMPTY_ARRAY;
  const washroomOccupancy = useOccupancy(occupancyWashrooms, activeToken, API_BASE);

  // Reset carousel index if it goes out of bounds
  useEffect(() => {
    if (washroomsWithSwaasData.length > 0 && carouselIndex >= washroomsWithSwaasData.length) {
      setCarouselIndex(0);
    }
  }, [washroomsWithSwaasData.length, carouselIndex]);

  // Auto-select when only one washroom is available — skip the picker entirely.
  // Two sources to consider:
  //   1. API-driven (Floor asset returns washrooms[]): pick washroomsFromApi[0].id
  //   2. Config-driven (features.washroom_select=true + config.washroom_ids):
  //      pick the single key from washroom_ids (e.g. "men" or "women")
  // Only runs while washroom is unset — won't override an explicit selection.
  useEffect(() => {
    if (washroom) return;
    if (washroomsFromApi.length === 1) {
      setWashroom(washroomsFromApi[0].id);
      return;
    }
    if (showWashroomSelect && config?.washroom_ids) {
      const keys = Object.keys(config.washroom_ids);
      if (keys.length === 1) setWashroom(keys[0]);
    }
  }, [washroomsFromApi, showWashroomSelect, config?.washroom_ids, washroom]);

  // Auto-rotate SWaaS score carousel for Floor assets (every 5 seconds)
  // Only rotate through washrooms that have data
  useEffect(() => {
    if (submitted) return; // ✅ pause during SubmissionNotice

    const count = washroomsWithSwaasData.length;
    if (count <= 1 || rating) return;

    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % count);
    }, 5000);

    return () => clearInterval(interval);
  }, [washroomsWithSwaasData.length, rating, submitted]);

  // Auto-reset: go back to home screen after 1 minute of *inactivity*.
  // The timer restarts whenever the user interacts (rating, reasons, comment,
  // washroom selection, name fields). Typing a long name (>60s) used to fire
  // handleReset mid-edit and wipe the form — adding firstName/lastName here
  // keeps the timer in sync with the actual user activity.
  useEffect(() => {
    if (!rating || submitted) return;

    const timer = setTimeout(() => {
      handleReset();
    }, 60 * 1000); // 1 minute

    return () => clearTimeout(timer);
  }, [rating, submitted, reasons, additionalComment, washroom, showDetails, firstName, lastName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Optional: friendly label to display to the user
  const effectiveWashroomLabel =
    backendLabel ||
    (washroom
      ? washroomsFromApi.find((w) => w.id === washroom)?.name ||
        config?.washroom_labels?.[washroom] ||
        (washroom === 'men'
          ? "Men's Washroom"
          : washroom === 'women'
            ? "Women's Washroom"
            : 'Selected Washroom')
      : null);

  // Admin contact — priority order:
  //   1. Kiosk admin override (MainActivity's AndroidDevice JS bridge)
  //   2. Bundled config.json value
  //   3. null (caller falls back to the hardcoded hotline default)
  //
  // window.AndroidDevice is only present when we run inside the kiosk APK
  // WebView — for the browser PWA path it's undefined and we silently fall
  // through to config.json. Wrapped in try/catch because bridge calls can
  // throw in rare WebView lifecycle edge cases and we never want a thrown
  // exception here to blank out the whole page.
  let bridgePhone = null;
  try {
    if (typeof window !== 'undefined'
        && window.AndroidDevice
        && typeof window.AndroidDevice.getSupportPhone === 'function') {
      const v = window.AndroidDevice.getSupportPhone();
      if (v && typeof v === 'string' && v.trim()) bridgePhone = v.trim();
    }
  } catch (e) {
    console.warn('AndroidDevice.getSupportPhone unavailable:', e);
  }
  // Resolution chain (most specific → fallback):
  //   1. bridgePhone — per-tablet admin override via AndroidDevice bridge,
  //      set by the kiosk app's "Change Support Number" admin option.
  //   2. branding?.supportPhone — per-customer Octacle SERVER_SCOPE attribute,
  //      fetched server-side and shipped on /api/ref/validate's branding obj.
  //   3. config.admin_support.phone — bundled config.json fallback for
  //      browser/PWA deployments that don't go through Octacle attributes.
  //   4. MYHUB_DEFAULT_SUPPORT_PHONE — last-resort MyHub support line.
  const supportPhone =
    bridgePhone
    || branding?.supportPhone
    || config?.admin_support?.phone
    || MYHUB_DEFAULT_SUPPORT_PHONE;

  useEffect(() => {
    // If already have cached result, use it
    if (configCache.result) {
      setConfig(configCache.result);
      return;
    }

    // If fetch is already in progress, wait for it
    if (configCache.promise) {
      configCache.promise.then((data) => setConfig(data));
      return;
    }

    // Start new fetch and cache the promise. Use a relative path so the
    // request resolves against the document's origin — works for the hosted
    // PWA (/config.json on servicedesk.swaas.myhub.co.in) and for the kiosk
    // APK (config.json sitting beside index.html inside assets/web/).
    configCache.promise = fetch('./config.json')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        configCache.result = data;
        return data;
      })
      .catch((err) => {
        console.error('❌ Failed to load config.json', err);
        const errorState = { _loadError: true };
        // Clear cache on error to allow retry
        configCache.promise = null;
        configCache.result = null;
        return errorState;
      });

    configCache.promise.then((data) => setConfig(data));
  }, []);

  // Show header immediately, then load content progressively
  if (!configLoaded) {
    return (
      <div className="App" style={appStyle}>
        <AppHeader locationLabel={backendLabel} branding={branding} />
        <main className="page">
          <div className="animate-pulse space-y-5 mt-6 px-2">
            <div className="h-8 bg-gray-200 rounded-xl mx-auto max-w-xs" />
            <div className="h-4 bg-gray-200 rounded-lg mx-auto max-w-[200px]" />
            <div className="flex justify-center gap-8 pt-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="w-20 h-20 bg-gray-200 rounded-full" />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ── Service-Request mode (Blackstone kiosk) ──────────────────────────────
  // Must come AFTER config loads (needs config.device_id / location) but
  // BEFORE the require_refid gate — the service-request view doesn't need
  // a validated refId to display. Ref data (label, tenantId, etc.) arrives
  // asynchronously and is passed down; ServiceRequestApp handles null/undefined.
  if (isServiceRequestMode) {
    return (
      <ServiceRequestApp
        refId={refId}
        locationLabel={backendLabel}
        branding={branding}
        config={config}
        features={refState.data?.features}
        tenantId={refState.data?.tenantId}
        customerId={refState.data?.customerId}
      />
    );
  }

  const toggleReason = (reason) => {
    setReasons((prev) => {
      const isRemoving = prev.includes(reason);
      const newReasons = isRemoving ? prev.filter((r) => r !== reason) : [...prev, reason];

      // Track reason selection (only when adding, not removing)
      if (!isRemoving) {
        analytics.reasonSelected(reason, rating, { refId });
      }

      // Auto-show comment box when "Others" is selected
      if (reason === 'Others' && !isRemoving) {
        setShowDetails(true);
      }

      return newReasons;
    });
  };

  // Shared post-submission flow: show the Thanks notice, schedule the auto-
  // return to Home, and clear form fields ready for the next user. Called
  // from both the success path (delivery confirmed) and the catch path
  // (queued for background retry) — the on-screen UX is identical because
  // from the user's perspective, both outcomes mean "your feedback is
  // recorded, please move on." Extracting this dedupes 8 lines that
  // previously diverged on every change.
  const finishSubmission = (submittedRating) => {
    setLastRating(submittedRating);
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => handleReset(), 10000);
    setRating('');
    setReasons([]);
    setAdditionalComment('');
    setWashroom('');
    setFirstName('');
    setLastName('');
  };

  const handleSubmit = async () => {
    // A double tap fires twice before isSubmitting re-renders the button
    // disabled; the server no longer merges two taps with different times.
    if (submittingRef.current) return;
    if (!configReady) {
      alert('Configuration not loaded yet. Please wait a moment.');
      return;
    }

    // Check if reasons are required for Poor rating
    if (rating === 'Poor' && reasons.length === 0) {
      setSubmitAttempted(true);

      document
        .getElementById('reasonsFieldset')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Comment is required when "Others" is selected
    if (reasons.includes('Others') && !additionalComment.trim()) {
      // Mark the attempt so the inline error below the comment box renders,
      // matching the "select at least one reason" and name validations.
      setSubmitAttempted(true);
      document
        .getElementById('additionalComment')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('additionalComment')?.focus();
      return;
    }

    // Each field is checked only if IT is required — one may block submit while
    // the other stays optional. Focus lands on the first offending field.
    if (requireFirstName || requireLastName) {
      const firstBad = requireFirstName && firstName.trim().length < FIRST_NAME_MIN_LEN;
      const lastBad = requireLastName && lastName.trim().length < LAST_NAME_MIN_LEN;
      if (firstBad || lastBad) {
        setSubmitAttempted(true);
        const target = firstBad ? 'firstName' : 'lastName';
        document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        document.getElementById(target)?.focus();
        return;
      }
    }

    // Only enforce selection when the feature is enabled or washrooms are available
    if (shouldShowWashroomSelect && !washroomId) {
      setWashroomError(true);
      document
        .getElementById('washroomFieldset')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Optional: front-end guard if you enforce presence strictly
    if (requireRef && !refId) {
      alert(
        'This link is invalid: missing reference id. Please try again with a valid URL / QR code.'
      );
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    // Guaranteed-fresh token for the n8n incident path, read from the singleton
    // at submit time so n8n can create the octacle alarm with a valid token.
    const freshTbToken = urlToken || (await ensureFresh().catch(() => null)); // never leave submit stuck

    const feedbackData = {
      // #3: client-generated idempotency key, stable across every retry of this
      // submission, so the server can dedup a replay reliably (window- and
      // restart-independent) instead of relying on the 30-min in-memory fingerprint.
      idempotencyKey: (window.crypto?.randomUUID?.()
        || `${Date.now()}-${Math.random().toString(36).slice(2)}`),
      solution: config.solution || 'SWS',
      rating,
      reasons,
      additionalComment,
      device_id: config.device_id || 'UNKNOWN_DEVICE',
      location: config.location || 'UNKNOWN_LOCATION',
      washroomId: washroomId || null,
      timestamp: new Date().toISOString(),
      browser: navigator.userAgent,
      hourOfDay: new Date().getHours(),
      refId, // include the reference id (can be null; backend decides policy)
      tbToken: freshTbToken, // URL token takes priority (for E2E testing)
      ...(collectName && {
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
      }),
    };

    try {
      const resp = await axios.post(`${API_BASE}/submitFeedback`, feedbackData, { timeout: 5000 });

      const { ok, code, message } = resp?.data || {};
      if (!ok) {
        const ErrorCopy = {
          MISSING_REFID: 'Please scan a valid QR code to continue.',
          INVALID_REFID: 'This washroom is not recognized or inactive.',
          VALIDATOR_UNAVAILABLE: 'Can’t validate right now. Please try again.',
          TIMEOUT: 'Validation timed out. Please retry.',
          UPSTREAM: 'Service temporarily unavailable.',
          EXCEPTION: 'Something went wrong. Please retry.',
          SERVER_ERROR: 'Server error. Please try again.',
        };
        const friendly = ErrorCopy[code] || message || 'Could not submit feedback';
        alert(friendly);
        return; // don’t clear the form on handled errors
      }

      // ✅ SUCCESS — direct delivery
      analytics.feedbackSubmitted(feedbackData);
      finishSubmission(rating);
    } catch (err) {
      // Network / server error. Queue silently and show the user "Thanks" so
      // they can leave the kiosk; submissionQueue.start() will drain when
      // connectivity returns. Original timestamp (set above) is preserved on
      // the payload so dashboard time-of-feedback stays accurate.
      console.warn('[handleSubmit] network/server error, queueing for retry:', err?.message);
      // Don't persist large, soon-stale fields in the offline queue — the
      // tbToken would expire during a long outage, and the userAgent is static
      // noise. Shrinks each stored item. A FRESH tbToken is re-attached at drain
      // time by the enricher in start() above, so incident classification still
      // works; the idempotencyKey + original timestamp keep the replay safe and
      // time-accurate.
      submissionQueue.enqueue('/submitFeedback', { ...feedbackData, tbToken: undefined, browser: undefined });
      analytics.feedbackSubmitted(feedbackData);
      finishSubmission(rating);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setRating('');
    setReasons([]);
    setAdditionalComment('');
    setSubmitted(false);
    setWashroom('');
    setFirstName('');
    setLastName('');
    setShowDetails(false);
    setWashroomError(false);
    setSubmitAttempted(false);

    // Track feedback reset
    analytics.feedbackReset();

    // Scroll back to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Check if asset is Floor type (enables task management)
  const isFloorAsset = refState.status === 'ok' && refState.data?.assetProfileName === 'Floor';

  // Check if asset is a Washroom (direct washroom ref link)
  const isWashroomAsset =
    refState.status === 'ok' && refState.data?.assetProfileName === 'Washroom';

  // Anything else = invalid ref type
  const isValidAsset = isFloorAsset || isWashroomAsset;

  // 🔁 Feature toggle for Task Management
  const isTaskManagementEnabled =
    (config?.features?.task_management ?? TASK_MGMT_FEATURE_DEFAULT) && isValidAsset;

  // ✅ Backend-gated render (prevents emoji flicker)
  if (!showWashroomSelect && requireRef) {
    if (!configLoaded || refState.status === 'loading') {
      return (
        <>
          <AppHeader branding={branding} />
          <main className="page">
            <TopProgress visible />
          </main>
        </>
      );
    }

    // 🚫 Ref validation succeeded, but asset type is not Floor or Washroom
    if (refState.status === 'ok' && !isValidAsset) {
      return (
        <>
          <AppHeader branding={branding} />
          <main className="page">
            <FriendlyError code="INVALID_REFID" supportPhone={supportPhone} variant="card" />
          </main>
        </>
      );
    }

    // Terminal codes (MISSING_REFID, INVALID_REFID, ID_MISMATCH) are fatal —
    // either there's no refId, the asset doesn't exist on TB, or upstream
    // returned a different id than we asked for. Retrying won't change the
    // answer, and submitting feedback against a broken refId would just
    // pollute Mongo with orphan rows. Render the FriendlyError card and
    // refuse the form. For transient failures (UPSTREAM, TIMEOUT, EXCEPTION,
    // NETWORK, …) the form still renders with safe defaults and the
    // ReconnectingBanner shows so the operator has a visible signal.
    if (refState.status === 'error' && TERMINAL_REF_CODES.has(refState.code)) {
      return (
        <>
          <AppHeader branding={branding} />
          <main className="page">
            <FriendlyError
              code={refState.code}
              supportPhone={supportPhone}
              variant="card"
            />
          </main>
        </>
      );
    }
  }

  // Scroll to the details block (reasons/comment/buttons) with header offset.
  // Skips if user prefers reduced motion or we're already near the target.
  const smoothScrollToDetails = (nextRating) => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    const el = detailsRef.current;
    if (!el) return;

    const headerH = (document.querySelector('.hero')?.offsetHeight || 0) + 8;

    // IMPORTANT: use nextRating if provided, else fallback to state
    const effectiveRating = nextRating ?? rating;

    // Poor needs bigger peek
    const peekTop = effectiveRating === 'Poor' ? 80 : 100;

    const elTop = el.getBoundingClientRect().top + window.scrollY;
    const targetY = Math.max(0, elTop - headerH - peekTop);

    if (Math.abs(window.scrollY - targetY) < 24) return;

    window.scrollTo({ top: targetY, behavior: 'smooth' });

    setTimeout(() => {
      const focusable = el.querySelector('button, textarea, [tabindex]:not([tabindex="-1"])');
      focusable?.focus?.({ preventScroll: true });
    }, 250);
  };

  const onEmojiSelect = (value) => {
    if (rating === value) {
      setRating('');
      setReasons([]);
      setAdditionalComment('');
      setSubmitAttempted(false);
      setWashroomError(false);
      setFirstName('');
      setLastName('');
      setWashroom('');
      return;
    }
    setRating(value);
    setReasons([]);
    setAdditionalComment('');
    setSubmitAttempted(false);
    setWashroomError(false);
    setFirstName('');
    setLastName('');
    setWashroom('');

    // Track rating selection
    analytics.ratingSelected(value, { refId, washroomId: washroomId || null });

    requestAnimationFrame(() => {
      setTimeout(() => {
        const submitEl = submitBtnRef.current;
        if (submitEl) {
          const rect = submitEl.getBoundingClientRect();
          if (rect.bottom > window.innerHeight) {
            submitEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
            return;
          }
        }
        smoothScrollToDetails(value);
      }, 0);
    });
  };

  const clearRating = () => {
    setRating('');
    setReasons([]);
    setAdditionalComment('');
    setShowDetails(false);
    setWashroom('');
    setFirstName('');
    setLastName('');
    setSubmitAttempted(false);
    setWashroomError(false);
    // smooth scroll back to the emoji row
    setTimeout(() => {
      emojiRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  };

  const getVariantForRating = (r) =>
    r === 'Excellent' ? 'veryPositive' : r === 'Good' ? 'positive' : 'negative';

  // Compute the washroomId we will submit
  // - Floor asset: user must select from washroomsFromApi (or config mapping)
  // - Washroom asset: refId itself is the washroom id
  const washroomId =
    washroomsFromApi.length > 0
      ? washroom // ID from API washrooms (Floor asset)
      : showWashroomSelect && washroom && config?.washroom_ids
        ? config.washroom_ids[washroom] || ''
        : isWashroomAsset && refId
          ? refId // direct washroom link -> refId is washroomId
          : '';

  // Task Management Handlers
  const handleStaffLoginClick = () => {
    analytics.staffLoginClicked();
    setShowTaskManagement(true);
  };

  const handleBackToFeedback = () => {
    setShowTaskManagement(false);
    setTaskAuthCompleted(false);
  };

  const handleStaffPinValidated = (staffData) => {
    setStaffInfo(staffData);
    setTaskAuthCompleted(true);
  };

  // Render Task Management views
  if (showTaskManagement && isTaskManagementEnabled) {
    if (!taskAuthCompleted) {
      return (
        <>
          <StaffPinEntry
            onPinValidated={handleStaffPinValidated}
            onBackHome={handleBackToFeedback}
            tenantId={refState.data?.tenantId}
            customerId={refState.data?.customerId}
          />
          <div className="version-footer">
            <span className="version-text">v{packageJson.version}</span>
          </div>
          <div className="powered-by-footer">
            <span className="powered-by-text">Powered by</span>
            <img src="./logos/myhub.png" alt="myHuB" className="powered-by-logo" />
          </div>
        </>
      );
    }
    return (
      <>
        <TaskList
          assetId={refState.data?.assetId}
          onBackHome={handleBackToFeedback}
          staffInfo={staffInfo}
          tenantId={refState.data?.tenantId}
          customerId={refState.data?.customerId}
        />
        <div className="version-footer">
          <span className="version-text">v{packageJson.version}</span>
        </div>
        <div className="powered-by-footer">
          <span className="powered-by-text">Powered by</span>
          <img src="./logos/myhub.png" alt="myHuB" className="powered-by-logo" />
        </div>
      </>
    );
  }

  const envMode = process.env.REACT_APP_ENV_MODE || 'production';
  const isSandboxMode = envMode === 'sandbox' || envMode === 'test' || envMode === 'development';

  return (
    <div className="App" style={appStyle}>
      {/* Sandbox Mode Banner */}
      {isSandboxMode && (
        <div className="sandbox-banner">
          <span className="sandbox-icon">🧪</span>
          <span className="sandbox-text">SANDBOX MODE - Test Environment</span>
        </div>
      )}

      {!submitted ? (
        <>
          <AppHeader locationLabel={backendLabel} branding={branding} />

          {/* QR Touchless bar - only show when launched from kiosk app */}
          {/* Moved below main content as a bottom bar */}

          <main className="page">
            <ReconnectingBanner
              visible={refState.status === 'error' && !TERMINAL_REF_CODES.has(refState.code)}
            />
            {/* Question prompt above emojis */}
            {(() => {
              return (
                <h2 className="prompt prompt--color">
                  <span className="q-text">How was your washroom visit today?</span>
                </h2>
              );
            })()}

            {/* Emoji row */}
            <div ref={emojiRowRef} className="flex justify-center gap-4 sm:gap-6 my-4 sm:my-6 px-1">
              {[
                {
                  emoji: '😄',
                  label: 'Excellent',
                  value: 'Excellent',
                  selectedCard: 'border-green-400 bg-green-50 shadow-md shadow-green-100',
                  selectedEmojiBg: 'bg-green-100',
                  selectedLabel: 'text-green-600 font-bold',
                },
                {
                  emoji: '🙂',
                  label: 'Satisfactory',
                  value: 'Good',
                  selectedCard: 'border-blue-400 bg-blue-50 shadow-md shadow-blue-100',
                  selectedEmojiBg: 'bg-blue-100',
                  selectedLabel: 'text-blue-600 font-bold',
                },
                {
                  emoji: '🙁',
                  label: 'Poor',
                  value: 'Poor',
                  selectedCard: 'border-red-400 bg-red-50 shadow-md shadow-red-100',
                  selectedEmojiBg: 'bg-red-100',
                  selectedLabel: 'text-red-600 font-bold',
                },
              ].map((item) => {
                const isSelected = rating === item.value;
                if (rating && !isSelected) return null;
                return (
                  <div
                    key={item.value}
                    onClick={() => onEmojiSelect(item.value)}
                    className={`flex flex-col items-center justify-between gap-3 sm:gap-4 rounded-2xl sm:rounded-3xl cursor-pointer select-none transition-all duration-200 pt-5 pb-4 sm:pt-8 sm:pb-6 px-4 sm:px-6 flex-1 min-w-0 max-w-[200px] ${
                      isSelected
                        ? item.selectedCard
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className={`w-20 h-20 sm:w-28 sm:h-28 rounded-full flex items-center justify-center text-5xl sm:text-7xl transition-colors duration-200 ${
                        isSelected ? item.selectedEmojiBg : 'bg-gray-100'
                      }`}
                    >
                      {item.emoji}
                    </div>
                    <span
                      className={`text-sm sm:text-base tracking-wide transition-colors duration-200 ${
                        isSelected ? item.selectedLabel : 'text-gray-900 font-bold'
                      }`}
                    >
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Washroom Occupancy Strip - Only show for washrooms that have occupancy data */}
            {!rating && isFloorAsset && washroomsFromApi.length > 0 && Object.keys(washroomOccupancy).length > 0 && (
              <div className="occupancy-strip">
                <div className="occupancy-strip__icons">
                  {[...washroomsFromApi]
                    .sort((a, b) => {
                      // Women first (left), Men last (right)
                      const order = (name) => {
                        const n = (name || '').toLowerCase();
                        if (n.includes('women') || n.includes('woman') || n.includes('ladies')) return 0;
                        if (n.includes('unisex')) return 1;
                        return 2; // men
                      };
                      return order(a.name) - order(b.name);
                    })
                    .map((wr) => {
                      const occ = washroomOccupancy[wr.id];
                      if (occ == null) {
                        // Invisible placeholder to preserve position
                        return <div key={wr.id} className="occ-square" style={{ visibility: 'hidden' }} />;
                      }
                      const isOccupied = occ?.occupied;
                      return (
                        <div key={wr.id} className={`occ-square ${isOccupied ? 'occ-square--occupied' : 'occ-square--vacant'}`}>
                          {getWashroomIcon(wr.name, 120, isOccupied ? '#dc2626' : '#16a34a')}
                          <span className="occ-square__label">{isOccupied ? 'IN USE' : 'FREE'}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* SWaaS Score Carousel - For Floor assets, show each washroom's score */}
            {!rating && isFloorAsset && washroomsFromApi.length > 0 && showSwaasScore && (
              <>
                {/* Hidden gauges to fetch data and report status for ALL washrooms */}
                {washroomsFromApi.map((wr) => (
                  <div key={`fetch-${wr.id}`} style={{ display: 'none' }}>
                    <SwaasScoreGauge
                      assetId={wr.id}
                      tbToken={activeToken}
                      washroomName={wr.name}
                      onDataStatus={handleSwaasDataStatus}
                    />
                  </div>
                ))}
                {/* Visible carousel - only washrooms with data */}
                {washroomsWithSwaasData.length > 0 && (
                  <div className="swaas-carousel" style={{ position: 'relative' }}>
                    {washroomsWithSwaasData.map((wr, idx) => (
                      <div
                        key={wr.id}
                        style={{
                          opacity: idx === carouselIndex ? 1 : 0,
                          transition: 'opacity 0.5s ease-in-out',
                          position: idx === carouselIndex ? 'relative' : 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          pointerEvents: idx === carouselIndex ? 'auto' : 'none',
                        }}
                      >
                        <SwaasScoreGauge
                          assetId={wr.id}
                          tbToken={activeToken}
                          washroomName={wr.name}
                          washroomIcon={getWashroomIcon(wr.name, 48)}
                        />
                      </div>
                    ))}
                    {/* Carousel dots indicator - only show if more than one washroom has data */}
                    {washroomsWithSwaasData.length > 1 && (
                      <div className="flex justify-center gap-2 mt-2">
                        {washroomsWithSwaasData.map((wr, idx) => (
                          <button
                            key={wr.id}
                            onClick={() => setCarouselIndex(idx)}
                            className={`w-2 h-2 rounded-full p-0 border-0 cursor-pointer transition-all duration-200 ${
                              idx === carouselIndex ? 'bg-gray-700 scale-125' : 'bg-gray-300'
                            }`}
                            aria-label={`View ${wr.name}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            {/* SWaaS Score Gauge - For single Washroom assets */}
            {!rating && isValidAsset && washroomsFromApi.length === 0 && showSwaasScore && (
              <div className="swaas-single">
                <SwaasScoreGauge
                  assetId={refId}
                  tbToken={activeToken}
                />
              </div>
            )}

            {rating && (
              <div className="flex justify-center mt-3">
                <button
                  type="button"
                  onClick={clearRating}
                  className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-blue-500 transition-colors duration-150"
                >
                  ← Change rating
                </button>
              </div>
            )}

            {rating && (
              <div className="mt-6 space-y-4">
                <div ref={detailsRef} />

                {/* Washroom selector FIRST — only show when required (Floor assets) */}
                {shouldShowWashroomSelect && (
                  <div id="washroomFieldset" className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-center text-xs font-semibold uppercase tracking-widest text-gray-500 mb-3">
                      Which washroom?
                    </p>
                    <div className={`washroomCards${rating === 'Excellent' ? ' rating-excellent' : rating === 'Good' ? ' rating-good' : rating === 'Poor' ? ' rating-poor' : ''}`}>
                      {washroomsFromApi.length > 0 ? (
                        washroomsFromApi.map((wr) => (
                          <label
                            key={wr.id}
                            className={`washroomCard ${washroom === wr.id ? 'selected' : ''}`}
                          >
                            <input
                              type="radio"
                              name="washroom"
                              value={wr.id}
                              checked={washroom === wr.id}
                              onChange={() => {
                                setWashroom(wr.id);
                                setWashroomError(false);
                              }}
                              className="hiddenInput"
                            />
                            <div className="washroomEmoji">{getWashroomIcon(wr.name)}</div>
                            <div className="washroomLabel">{wr.name}</div>
                          </label>
                        ))
                      ) : (
                        <>
                          <label className={`washroomCard ${washroom === 'men' ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name="washroom"
                              value="men"
                              checked={washroom === 'men'}
                              onChange={() => setWashroom('men')}
                              className="hiddenInput"
                            />
                            <div className="washroomEmoji"><MdMan size={32} color="#3b82f6" /></div>
                            <div className="washroomLabel">Men's</div>
                          </label>
                          <label className={`washroomCard ${washroom === 'women' ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name="washroom"
                              value="women"
                              checked={washroom === 'women'}
                              onChange={() => setWashroom('women')}
                              className="hiddenInput"
                            />
                            <div className="washroomEmoji"><MdWoman size={32} color="#ec4899" /></div>
                            <div className="washroomLabel">Women's</div>
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {washroomError && (
                  <p className="flex items-center justify-center gap-1.5 text-sm text-red-600">
                    ⚠️ Please select a washroom above
                  </p>
                )}

                {/* Name capture: gated on customer-scope feature_collectName.
                    Rendered before the reasons / "tell us more" sections so the
                    user fills identity first; values persist if they then
                    change the rating (no clear-on-rating logic touches them). */}
                {collectName && (
                  <div className="grid grid-cols-1 gap-3 px-1 sm:grid-cols-2 sm:max-w-lg sm:mx-auto">
                    <div>
                      <label htmlFor="firstName" className="mb-2 block text-sm font-semibold text-gray-700">
                        {firstNameLabel}{requireFirstName ? <span className="text-red-500"> *</span> : ' (optional)'}
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        autoComplete={firstRelabelled ? 'off' : 'given-name'}
                        maxLength={firstMaxLen}
                        value={firstName}
                        onChange={(e) =>
                          setFirstName(e.target.value.replace(firstStrip, '').slice(0, firstMaxLen))
                        }
                        onFocus={(e) => {
                          setTimeout(() => {
                            e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }, 300);
                        }}
                        placeholder={firstNamePlaceholder}
                        className={`w-full rounded-xl border px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 transition-all duration-150 focus:outline-none focus:ring-2 ${
                          requireFirstName && submitAttempted && firstName.trim().length < FIRST_NAME_MIN_LEN
                            ? 'border-red-400 focus:border-red-400 focus:ring-red-200'
                            : 'border-gray-300 focus:border-blue-400 focus:ring-blue-200'
                        }`}
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="mb-2 block text-sm font-semibold text-gray-700">
                        {lastNameLabel}{requireLastName ? <span className="text-red-500"> *</span> : ' (optional)'}
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        autoComplete={lastRelabelled ? 'off' : 'family-name'}
                        maxLength={lastMaxLen}
                        value={lastName}
                        onChange={(e) =>
                          setLastName(e.target.value.replace(lastStrip, '').slice(0, lastMaxLen))
                        }
                        onFocus={(e) => {
                          setTimeout(() => {
                            e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }, 300);
                        }}
                        placeholder={lastNamePlaceholder}
                        className={`w-full rounded-xl border px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 transition-all duration-150 focus:outline-none focus:ring-2 ${
                          requireLastName && submitAttempted && lastName.trim().length < LAST_NAME_MIN_LEN
                            ? 'border-red-400 focus:border-red-400 focus:ring-red-200'
                            : 'border-gray-300 focus:border-blue-400 focus:ring-blue-200'
                        }`}
                      />
                    </div>
                    {/* Only the fields that are actually required AND actually
                        wrong get named — telling someone their optional
                        "Department" is invalid would be a lie. */}
                    {submitAttempted && (() => {
                      const problems = [
                        requireFirstName && firstName.trim().length < FIRST_NAME_MIN_LEN
                          && `${firstNameLabel} needs at least ${FIRST_NAME_MIN_LEN} characters`,
                        requireLastName && lastName.trim().length < LAST_NAME_MIN_LEN
                          && `${lastNameLabel} is required`,
                      ].filter(Boolean);
                      return problems.length > 0 ? (
                        <p className="col-span-full text-center text-sm text-red-500">
                          ⚠️ {problems.join('; ')}
                        </p>
                      ) : null;
                    })()}
                  </div>
                )}

                {/* For Poor rating - show reasons REQUIRED before submit */}
                {rating === 'Poor' && (
                  <div id="reasonsFieldset" className="space-y-3">
                    <h4 className="text-center text-base font-semibold text-gray-700">
                      🤔 What could have been better?{' '}
                      <span className="text-sm font-normal text-red-500">(required)</span>
                    </h4>
                    <div className="flex flex-wrap justify-center gap-2.5 px-4">
                      {negativeReasons.map((reason) => {
                        const isSelected = reasons.includes(reason);
                        return (
                          <button
                            key={reason}
                            onClick={() => toggleReason(reason)}
                            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-150 focus:outline-none active:scale-95 ${
                              isSelected
                                ? 'border-amber-500 bg-amber-500 text-white shadow-sm'
                                : 'border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-300 hover:bg-amber-100'
                            }`}
                          >
                            {isSelected ? '✓ ' : ''}{reason}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Collapsible details toggle - only for positive feedback */}
                {['Excellent', 'Good'].includes(rating) && !reasons.includes('Others') && (
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        const next = !showDetails;
                        setShowDetails(next);
                        requestAnimationFrame(() => {
                          setTimeout(() => smoothScrollToDetails(rating), 0);
                        });
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-dashed border-gray-300 text-sm text-gray-500 bg-gray-50 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-all duration-150"
                    >
                      <span className={`text-xs transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`}>▼</span>
                      {showDetails ? 'Hide details' : 'Want to tell us more?'}
                      {!showDetails && <span className="text-xs text-gray-400">(optional)</span>}
                    </button>
                  </div>
                )}

                {/* Positive reasons - show when toggled or Others selected */}
                {['Excellent', 'Good'].includes(rating) &&
                  (showDetails || reasons.includes('Others')) && (
                    <div className="space-y-3">
                      <h4 className="text-center text-base font-semibold text-gray-700">
                        👍 What did you like most?
                      </h4>
                      <div className="flex flex-wrap justify-center gap-2.5 px-4">
                        {positiveReasons.map((reason) => {
                          const isSelected = reasons.includes(reason);
                          return (
                            <button
                              key={reason}
                              onClick={() => toggleReason(reason)}
                              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-150 focus:outline-none active:scale-95 ${
                                isSelected
                                  ? 'border-green-600 bg-green-600 text-white shadow-sm'
                                  : 'border-green-200 bg-green-50 text-green-800 hover:border-green-300 hover:bg-green-100'
                              }`}
                            >
                              {isSelected ? '✓ ' : ''}{reason}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {/* Comment box - only when Others is selected */}
                {reasons.includes('Others') && (
                  <div className="px-1">
                    <label htmlFor="additionalComment" className="mb-2 block text-sm font-semibold text-gray-700">
                      💡 Anything else?
                    </label>
                    <textarea
                      id="additionalComment"
                      value={additionalComment}
                      onChange={(e) => setAdditionalComment(e.target.value)}
                      onFocus={(e) => {
                        // On kiosk tablets the on-screen keyboard covers the textarea.
                        // Delay scroll so the keyboard has time to appear and resize the viewport.
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      placeholder="Type your comment here..."
                      rows={2}
                      className="w-full resize-none rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 transition-all duration-150 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                    {submitAttempted && !additionalComment.trim() && (
                      <p className="mt-2 text-center text-sm text-red-500">
                        ⚠️ Please add a comment when selecting "Others"
                      </p>
                    )}
                  </div>
                )}

                {rating === 'Poor' && submitAttempted && reasons.length === 0 && (
                  <p className="text-center text-sm text-red-500">
                    ⚠️ Please select at least one reason above
                  </p>
                )}

                {/* Submit button */}
                <div className="button-row">
                  <button
                    onClick={handleSubmit}
                    disabled={!configReady || isSubmitting}
                    className="inline-flex min-w-[180px] items-center justify-center gap-2 rounded-xl bg-green-600 px-8 py-3 text-base font-semibold text-white shadow-md shadow-green-100 transition-all duration-150 hover:bg-green-700 hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      'Submit Feedback'
                    )}
                  </button>
                </div>

                <div ref={submitBtnRef} className="pb-2 text-center">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="text-xs text-gray-400 transition-colors duration-150 hover:text-gray-600"
                  >
                    ← Start over
                  </button>
                </div>
              </div>
            )}

            {/* STAFF LOGIN Button */}
            {isTaskManagementEnabled && !rating && (
              <div
                style={{
                  position: 'fixed',
                  bottom: '20px',
                  right: '20px',
                  zIndex: 100,
                }}
              >
                <button
                  className="staff-login-icon"
                  aria-label="Staff login"
                  onClick={handleStaffLoginClick}
                >
                  <svg className="lock-svg" viewBox="0 0 24 24">
                    <path
                      d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm6-6h-1V9a5 5 0 0 0-10 0v2H6a2 2 0 0 0-2 2v7a2
                    2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2ZM9 9a3 3 0 0 1 6 0v2H9V9Z"
                    />
                  </svg>
                </button>
              </div>
            )}

          </main>

          {/* Touchless Feedback QR — shared component; home screen only */}
          <TouchlessQR
            refId={refId}
            config={config}
            features={refState.data?.features}
            serviceDeskMode={refState.data?.serviceDeskMode}
            visible={!rating}
          />
        </>
      ) : (
        <SubmissionNotice
          variant={getVariantForRating(lastRating)}
          washroomLabel={effectiveWashroomLabel}
          onClose={handleReset}
        />
      )}

      {/* Version Footer */}
      <div className="version-footer">
        <span className="version-text">v{packageJson.version}</span>
      </div>
    </div>
  );
}

export default App;
